import { useMemo, type ReactNode } from 'react';
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { CompetitionFlag } from '../../atoms/CompetitionFlag';
import useTabs from '../../../hooks/useTabs';
import useLocalization from '../../../hooks/useLocalization';
import { mergeSx } from '../../../utils/styles';
import {
  DEFAULT_PRE_MATCH_WIDGET_SECTION_ORDER,
  INDIVIDUAL_PRE_MATCH_HIDDEN_TABS,
  INDIVIDUAL_PRE_MATCH_SPORT_IDS,
  PRE_MATCH_WIDGET_TABS,
  PRE_MATCH_WIDGET_TAB_LABEL_KEYS,
  PRE_MATCH_WIDGET_TAB_ICONS,
} from './constants';
import { renderDefaultTabContent } from './components/TabContent';
import TeamVisual from './components/TeamVisual';
import { formatDisplayDate, formatRoundLabel, getCountdown } from './helpers';
import { styles } from './styles';
import type { PreMatchWidgetProps, PreMatchWidgetSection, PreMatchWidgetTabId } from './types';

const PreMatchWidget = ({
  data,
  defaultTab = 'preview-summary',
  sectionOrder = DEFAULT_PRE_MATCH_WIDGET_SECTION_ORDER,
  renderTabContent,
  sx,
}: PreMatchWidgetProps) => {
  const { t } = useLocalization();
  const tabConfig = useMemo(
    () =>
      PRE_MATCH_WIDGET_TABS.filter(
        (tab) =>
          !INDIVIDUAL_PRE_MATCH_SPORT_IDS.has(data.sport?.id ?? '') || !INDIVIDUAL_PRE_MATCH_HIDDEN_TABS.has(tab.value)
      ).map((tab) => ({
        ...tab,
        label: t(PRE_MATCH_WIDGET_TAB_LABEL_KEYS[tab.value], {
          defaultValue: tab.label,
        }),
      })),
    [data.sport?.id, t]
  );
  const availableDefaultTab = tabConfig.some((tab) => tab.value === defaultTab) ? defaultTab : tabConfig[0].value;
  const { value: activeTab, onChange } = useTabs<PreMatchWidgetTabId>({
    defaultTab: availableDefaultTab,
    tabConfig,
  });
  const displayedTab = tabConfig.some((tab) => tab.value === activeTab) ? activeTab : availableDefaultTab;
  const activeTabLabel = tabConfig.find((tab) => tab.value === displayedTab)?.label ?? displayedTab;
  const kickoff = getCountdown(data.startDate, {
    day: t('preMatchWidget.countdown.day'),
    days: t('preMatchWidget.countdown.days'),
    hourShort: t('preMatchWidget.countdown.hourShort'),
    hoursShort: t('preMatchWidget.countdown.hoursShort'),
    minuteShort: t('preMatchWidget.countdown.minuteShort'),
    minutesShort: t('preMatchWidget.countdown.minutesShort'),
  });
  const content = renderTabContent
    ? renderTabContent(displayedTab, data)
    : renderDefaultTabContent({ tab: displayedTab, data, t });
  const hasVenueDetails = Boolean(
    data.venue && (data.venue.name || (data.venue.capacity !== null && data.venue.capacity !== undefined))
  );
  const roundInfo = formatRoundLabel(data.tournament.round, t('round', { defaultValue: 'Round' }));
  const isSummaryTab = displayedTab === 'preview-summary';

  const sections: Record<PreMatchWidgetSection, ReactNode> = {
    competition: (
      <Box sx={styles.kickoffStrip}>
        <Box sx={styles.kickoffTopRow}>
          <Box sx={styles.competitionRow}>
            <CompetitionFlag name={data.competition.name} width={24} height={24} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={styles.competitionPrimary}>{data.tournament.name}</Typography>
              <Typography sx={styles.competitionSecondary}>
                {[data.competition.name, roundInfo].filter(Boolean).join(' • ')}
              </Typography>
            </Box>
          </Box>
          <Box sx={styles.kickoffMeta} data-pmw-close-safe-zone='true'>
            <Typography sx={styles.kickoffPrimary}>{formatDisplayDate(data.startDate)}</Typography>
            <Typography sx={styles.kickoffSecondary}>{dayjs(data.startDate).format('dddd')}</Typography>
          </Box>
        </Box>
        {hasVenueDetails ? (
          <Box sx={styles.venueRow}>
            <Typography sx={styles.venueInfo}>{data.venue?.name ?? '-'}</Typography>
            <Typography sx={styles.venueInfo}>
              {t('preMatchWidget.venueCapacity')}{' '}
              {data.venue?.capacity !== null && data.venue?.capacity !== undefined
                ? data.venue.capacity.toLocaleString()
                : '-'}
            </Typography>
          </Box>
        ) : null}
      </Box>
    ),
    tournament: (
      <Box sx={styles.teamStrip}>
        <Stack sx={styles.teamList}>
          <Box sx={styles.teamListRow}>
            <TeamVisual competitionName={data.competition.name} team={data.fixture.home} sx={styles.stripTeamVisual} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={styles.teamListMeta}>{data.fixture.home.name}</Typography>
            </Box>
          </Box>
          <Box sx={styles.teamListRow}>
            <TeamVisual competitionName={data.competition.name} team={data.fixture.away} sx={styles.stripTeamVisual} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={styles.teamListMeta}>{data.fixture.away.name}</Typography>
            </Box>
          </Box>
        </Stack>
        <Box sx={styles.countdown}>
          <Typography sx={styles.countdownValue}>{kickoff.value}</Typography>
          <Typography sx={styles.countdownLabel}>{kickoff.label}</Typography>
        </Box>
      </Box>
    ),
    content: (
      <Box
        key={displayedTab}
        sx={mergeSx(styles.contentCard, isSummaryTab && styles.summaryContentCard)}
        role='tabpanel'
        aria-label={activeTabLabel}
        data-summary-panel={isSummaryTab ? 'true' : undefined}
        data-scrollable-panel={!isSummaryTab ? 'true' : undefined}
      >
        {content}
      </Box>
    ),
    tabs: (
      <Box sx={styles.tabsShell}>
        <Tabs value={displayedTab} onChange={onChange} variant='fullWidth' sx={styles.tabs}>
          {tabConfig.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              aria-label={tab.label}
              title={tab.label}
              icon={PRE_MATCH_WIDGET_TAB_ICONS[tab.value]}
              sx={styles.iconTab}
            />
          ))}
        </Tabs>
      </Box>
    ),
  };

  return (
    <Box
      sx={mergeSx(styles.root, styles.constrainedRoot, isSummaryTab && styles.summaryRoot, sx)}
      data-summary-sizing={isSummaryTab ? 'true' : undefined}
    >
      <Stack sx={mergeSx(styles.container, isSummaryTab && styles.summaryContainer)}>
        {sectionOrder.map((section) => {
          const sectionContent = sections[section];

          if (!sectionContent) return null;

          return (
            <Box key={section} data-pmw-section={section}>
              {sectionContent}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default PreMatchWidget;
